/* eslint-disable */
import { Injectable } from '@angular/core';
import { Util } from '@libs/utilities/util';
import { AbstractExtensionPattern } from '@libs/libraries/shared';

import { NgCustomizableSliderComponent } from '../customizable-slider.component';
import { NgSliderHandleDirective } from '../directives/slider-handle.directive';
import { NgSliderLabelDirective } from '../directives/slider-label.directive';
import { EnumNgLabelType, EnumNgPointerType } from '../shared/customizable-slider.interface';
import { NgCustomizableSliderOptions, NgInputModelChange, NgModelValues, NgOutputModelChange } from '../shared/customizable-slider.data';


@Injectable()
export class NgSliderValueUpdateExtension extends AbstractExtensionPattern<NgCustomizableSliderComponent> {


	/**------------------------------------------------------
	 * Apply & Publish Modal Changes
	 */
	applyInputModelChange(modelChange: NgInputModelChange) {

		//0 - normalized change and modal change
		const normalizedModelChange	: NgModelValues = this.normalizedModelValues(modelChange);
		const normalizationChange	: boolean 	  = !NgModelValues.compare(modelChange, normalizedModelChange);
		if (normalizationChange) {
			this.main.value 	 = normalizedModelChange.value;
			this.main.highValue = normalizedModelChange.highValue;
		}

		//1 - update view low and high value
		this.main.viewLowValue = this.main.sliderHelperExtension.modelValueToViewValue(normalizedModelChange.value);
		this.main.viewHighValue = (this.main.range)
			? this.main.sliderHelperExtension.modelValueToViewValue(normalizedModelChange.highValue)
			: this.main.viewHighValue = null!

		//2 - update low and high handle
		this.main.sliderHandlerExtension.updateLowHandle(this.main.sliderHelperExtension.valueToPosition(this.main.viewLowValue));
		if (this.main.range) this.main.sliderHandlerExtension.updateHighHandle(this.main.sliderHelperExtension.valueToPosition(this.main.viewHighValue));

		//3 - update slider state
		this.updateSelectionBar();
		this.main.sliderTickHelperExtension.updateTicksScale();
		this.updateAriaAttributes();
		if (this.main.range) this.updateCombinedLabel();

		//4 - emit model change subject
		this.main.outputModelChangeSubject.next({
			value: normalizedModelChange.value,
			highValue: normalizedModelChange.highValue,
			forceChange: normalizationChange,
			userEventInitiated: false
		});
  	}

	//** push output model change */
	publishOutputModelChange(modelChange: NgOutputModelChange) {
		if (modelChange.userEventInitiated) {
			this.emitOutputs(modelChange);
			this.main.userChange.emit(this.main.sliderHelperExtension.getChangeContext());
		} else {
			setTimeout(() => this.emitOutputs(modelChange))
		}
	}


	/**------------------------------------------------------
	 * Normalized and Re-Normalized Modal Values
	 */
	reNormalizedModelValues() {

		//0 - update previous model value
		const previousModelValues: NgModelValues = {
			value		: this.main.value,
			highValue	: this.main.highValue
		};

		//1 - get normalized model value
		const normalizedModelValues: NgModelValues = this.normalizedModelValues(previousModelValues);

		//2 - if compare then exit
		if (NgModelValues.compare(normalizedModelValues, previousModelValues)) return;

		//3 - update value, highValue and subject
		this.main.value = normalizedModelValues.value;
		this.main.highValue = normalizedModelValues.highValue;
		this.main.outputModelChangeSubject.next({
			value				: this.main.value,
			highValue			: this.main.highValue,
			forceChange			: true,
			userEventInitiated	: false
		});
	}


	/**------------------------------------------------------
	 * Apply Functions
	 */
	applyOptions() {

		//0 - initialize view options
		this.main.viewOptions = new NgCustomizableSliderOptions();
		Object.assign(this.main.viewOptions, this.main.options);

		//1 - update draggable
		this.main.viewOptions.draggableRange 	 = (this.main.range && this.main.viewOptions.draggableRange);
		this.main.viewOptions.draggableRangeOnly = (this.main.range && this.main.viewOptions.draggableRangeOnly);
		if (this.main.viewOptions.draggableRangeOnly) this.main.viewOptions.draggableRange = true;

		//2 - update ticks
		this.main.viewOptions.showTicks = (this.main.viewOptions.showTicks || this.main.viewOptions.showTicksValues || Util.Basic.isDefined(this.main.viewOptions.ticksArray));
		if (this.main.viewOptions.showTicks && (Util.Basic.isDefined(this.main.viewOptions.tickStep) || Util.Basic.isDefined(this.main.viewOptions.ticksArray))) this.main.intermediateTicks = true;

		//3 - set show selection bar status
		this.main.viewOptions.showSelectionBar = (this.main.viewOptions.showSelectionBar || this.main.viewOptions.showSelectionBarEnd || Util.Basic.isDefined(this.main.viewOptions.showSelectionBarFromValue));

		//4 - apply steps array options
		if (Util.Basic.isDefined(this.main.viewOptions.stepsArray)) this.applyStepsArrayOptions();
			else this.applyFloorCeilOptions();

		//5 - combine labels
		if (Util.Basic.isUndefined(this.main.viewOptions.combineLabels)) {
			this.main.viewOptions.combineLabels = (minValue: string, maxValue: string): string => {
				return (minValue + ' - ' + maxValue);
			};
		}

		//6 - if no floor value
		if (this.main.viewOptions.logScale && this.main.viewOptions.floor === 0) throw new Error(`SliderValueUpdateExtension => applyOptions => FATAL ERROR: Can't use floor=0 with logarithmic scale`);
	}


	/**------------------------------------------------------
	 * Slider Actions
	 */
	resetSlider(rebindEvents: boolean = true) {

		//0 - manage element style, add accessibility for keyboard control
		this.main.sliderHandlerExtension.manageElementsStyle();
		this.addAccessibility();

		//1 - update floor and ceil value
		this.updateCeilLabel();
		this.updateFloorLabel();

		//2 - if rebind events
		if (rebindEvents) {
			this.main.unbindEvents();
			this.main.sliderHandlerExtension.manageEventsBindings();
		}

		//3 - update slider state
		this.main.sliderHandlerExtension.updateDisabledState();
		this.main.sliderHandlerExtension.updateAriaLabel();
		this.calculateViewDimensions();
		this.refocusPointerIfNeeded();
	}

	focusPointer(pointerType: EnumNgPointerType) {

		//0 - set pointer type
		if (pointerType !== EnumNgPointerType.Min && pointerType !== EnumNgPointerType.Max) pointerType = EnumNgPointerType.Min;

		//1 - focus element
		if (pointerType === EnumNgPointerType.Min) {
			this.main.minHandleElement.focus();
		} else if (this.main.range && pointerType === EnumNgPointerType.Max) {
			this.main.maxHandleElement.focus();
		}
	}


	/**------------------------------------------------------
	 * Attributes Helpers
	 */
	addAccessibility() {

		//0 - update area attributes
		this.updateAriaAttributes();

		//1 - update role of element and set focus
		this.main.minHandleElement.role = 'slider';
		if (this.main.viewOptions.keyboardSupport && !(this.main.viewOptions.readOnly || this.main.viewOptions.disabled)) {
			this.main.minHandleElement.tabindex = '0';
		} else {
			this.main.minHandleElement.tabindex = '';
		}

		//2 - set element orientation
		this.main.minHandleElement.ariaOrientation = (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0)
			? 'vertical'
			: 'horizontal';

		//3 - update aria label or area label by
		if (Util.Basic.isDefined(this.main.viewOptions.ariaLabel)) {
			this.main.minHandleElement.ariaLabel = this.main.viewOptions.ariaLabel!;
		} else if (Util.Basic.isDefined(this.main.viewOptions.ariaLabelledBy)) {
			this.main.minHandleElement.ariaLabelledBy = this.main.viewOptions.ariaLabelledBy!;
		}

		//4 - update element if range
		if (this.main.range) {

			//a. update role of element and set focus
			this.main.maxHandleElement.role = 'slider';
			if (this.main.viewOptions.keyboardSupport && !(this.main.viewOptions.readOnly || this.main.viewOptions.disabled)) {
				this.main.maxHandleElement.tabindex = '0';
			} else {
				this.main.maxHandleElement.tabindex = '';
			}

			//b. set orientation and update aria label or area label by
			this.main.maxHandleElement.ariaOrientation = (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0)
				? 'vertical'
				: 'horizontal';

			if (Util.Basic.isDefined(this.main.viewOptions.ariaLabelHigh)) {
				this.main.maxHandleElement.ariaLabel = this.main.viewOptions.ariaLabelHigh!;
			} else if (Util.Basic.isDefined(this.main.viewOptions.ariaLabelledByHigh)) {
				this.main.maxHandleElement.ariaLabelledBy = this.main.viewOptions.ariaLabelledByHigh!;
			}
		}
	}

	updateAriaAttributes() {

		//0 - update default area attributes
		this.main.minHandleElement.ariaValueNow  = (+this.main.value).toString();
		this.main.minHandleElement.ariaValueText = this.main.viewOptions.translate!(+this.main.value, EnumNgLabelType.Low);
		this.main.minHandleElement.ariaValueMin  = this.main.viewOptions.floor!.toString();
		this.main.minHandleElement.ariaValueMax  = this.main.viewOptions.ceil!.toString();

		//1 - update area attributes on valid range
		if (this.main.range) {
			this.main.maxHandleElement.ariaValueNow  = (+this.main.highValue).toString();
			this.main.maxHandleElement.ariaValueText = this.main.viewOptions.translate!(+this.main.highValue, EnumNgLabelType.High);
			this.main.maxHandleElement.ariaValueMin  = this.main.viewOptions.floor!.toString();
			this.main.maxHandleElement.ariaValueMax  = this.main.viewOptions.ceil!.toString();
		}
	}


	/**------------------------------------------------------
	 * Dimensions Helpers
	 */
	calculateViewDimensions() {

		//0 - set element dimension
		if (Util.Basic.isDefined(this.main.viewOptions.handleDimension)) this.main.minHandleElement.setDimension(this.main.viewOptions.handleDimension!);
			else this.main.minHandleElement.calculateDimension();

		//1 - handle width
		const handleWidth: number	  = this.main.minHandleElement.dimension;
		this.main.handleHalfDimension = handleWidth / 2;

		//2 - calculate dimension of full bar element
		if (Util.Basic.isDefined(this.main.viewOptions.barDimension)) this.main.fullBarElement.setDimension(this.main.viewOptions.barDimension!);
			else this.main.fullBarElement.calculateDimension();

		//3 - update max handle position
		this.main.maxHandlePosition = this.main.fullBarElement.dimension - handleWidth;
		if (this.main.initHasRun) {
			this.updateFloorLabel();
			this.updateCeilLabel();
			this.main.sliderHandlerExtension.initHandles();
		}
	}

	calculateViewDimensionsAndDetectChanges() {
		this.calculateViewDimensions();
		if (!this.isRefDestroyed()) this.main.changeDetectionRef.detectChanges();
	}

	isRefDestroyed(): boolean {
		return (<any>this.main.changeDetectionRef)?.['destroyed'];
	}


	/**------------------------------------------------------
	 * Update Floor and Ceil Helpers
	 */
	updateFloorLabel() {

		//0 - if floor element hidden then exit
		if (Util.Basic.isUndefined(this.main?.floorLabelElement?.alwaysHide)) return;

		//1 - update floor label
		this.main.floorLabelElement.setValue(this.main.sliderHelperExtension.getDisplayValue(this.main.viewOptions.floor!, EnumNgLabelType.Floor));
		this.main.floorLabelElement.calculateDimension();

		//2 - update position
		const position: number = this.main.viewOptions.rightToLeft
			? (this.main.fullBarElement.dimension - this.main.floorLabelElement.dimension)
			: 0;
		this.main.floorLabelElement.setPosition(position);
	}

	updateCeilLabel() {

		//0 - ceil element is hidden then exit
		if (Util.Basic.isUndefined(this.main?.ceilLabelElement?.alwaysHide)) return;

		//1 - update ceil label
		this.main.ceilLabelElement.setValue(this.main.sliderHelperExtension.getDisplayValue(this.main.viewOptions.ceil!, EnumNgLabelType.Ceil));
		this.main.ceilLabelElement.calculateDimension();

		//2 - update position
		const position: number = this.main.viewOptions.rightToLeft
			? 0
			: this.main.fullBarElement.dimension - this.main.ceilLabelElement.dimension;
		this.main.ceilLabelElement.setPosition(position);
	}

	updateFloorAndCeilLabelsVisibility() {

		//0 - if pointer event is hidden
		if (this.main.viewOptions.hidePointerLabels) return;

		//1 - update label and combined label
		let floorLabelHidden		: boolean = false;
		let ceilLabelHidden			: boolean = false;
		const isMinLabelAtFloor		: boolean = this.isLabelBelowFloorLabel(this.main.minHandleLabelElement);
		const isMinLabelAtCeil		: boolean = this.isLabelAboveCeilLabel( this.main.minHandleLabelElement);
		const isMaxLabelAtCeil		: boolean = this.isLabelAboveCeilLabel( this.main.maxHandleLabelElement);
		const isCombinedLabelAtFloor: boolean = this.isLabelBelowFloorLabel(this.main.combinedLabelElement);
		const isCombinedLabelAtCeil	: boolean = this.isLabelAboveCeilLabel( this.main.combinedLabelElement);

		//2 - show / hide floor label
		if (isMinLabelAtFloor) {
			floorLabelHidden = true;
			this.main.floorLabelElement.hide();
		} else {
			floorLabelHidden = false;
			this.main.floorLabelElement.show();
		}

		//3 - show / hide ceil label
		if (isMinLabelAtCeil) {
			ceilLabelHidden = true;
			this.main.ceilLabelElement.hide();
		} else {
			ceilLabelHidden = false;
			this.main.ceilLabelElement.show();
		}

		//4 - show / hide range label
		if (this.main.range) {

			//a. ceil and floor value hidden status
			const hideCeil	: boolean = this.main.combinedLabelElement.isVisible() ? isCombinedLabelAtCeil  : isMaxLabelAtCeil;
			const hideFloor	: boolean = this.main.combinedLabelElement.isVisible() ? isCombinedLabelAtFloor : isMinLabelAtFloor;

			//b. ceil element status
			if (hideCeil) this.main.ceilLabelElement.hide();
				else if (!ceilLabelHidden) this.main.ceilLabelElement.show();

			//c. hide / show floor label
			if (hideFloor) this.main.floorLabelElement.hide();
				else if (!floorLabelHidden) this.main.floorLabelElement.show();
		}
	}

	updateCombinedLabel() {

		//0 - update overlap status
		const isLabelOverlap: boolean = (this.main.viewOptions.rightToLeft)
			? this.main.minHandleLabelElement.position - this.main.minHandleLabelElement.dimension - 10 <= this.main.maxHandleLabelElement.position
			: this.main.minHandleLabelElement.position + this.main.minHandleLabelElement.dimension + 10 >= this.main.maxHandleLabelElement.position;

		//1 - if overlap
		if (isLabelOverlap) {

			//a. get value
			const lowDisplayValue	: string = this.main.sliderHelperExtension.getDisplayValue(this.main.viewLowValue, EnumNgLabelType.Low);
			const highDisplayValue	: string = this.main.sliderHelperExtension.getDisplayValue(this.main.viewHighValue, EnumNgLabelType.High);
			const combinedLabelValue: string = this.main.viewOptions.rightToLeft ? this.main.viewOptions.combineLabels!(highDisplayValue, lowDisplayValue) : this.main.viewOptions.combineLabels!(lowDisplayValue, highDisplayValue);

			//b. set combined label element
			this.main.combinedLabelElement.setValue(combinedLabelValue);
			const pos: number = this.main.viewOptions.boundPointerLabels ? Math.min(Math.max(this.main.selectionBarElement.position + this.main.selectionBarElement.dimension / 2 - this.main.combinedLabelElement.dimension / 2, 0), this.main.fullBarElement.dimension - this.main.combinedLabelElement.dimension) : this.main.selectionBarElement.position + this.main.selectionBarElement.dimension / 2 - this.main.combinedLabelElement.dimension / 2;

			//c. set position
			this.main.combinedLabelElement.setPosition(pos);
			this.main.minHandleLabelElement.hide();
			this.main.maxHandleLabelElement.hide();
			this.main.combinedLabelElement.show();
		}

		//2 - if no overlap
		else {

			//a. update low and high handle
			this.main.sliderHandlerExtension.updateHighHandle(this.main.sliderHelperExtension.valueToPosition(this.main.viewHighValue));
			this.main.sliderHandlerExtension.updateLowHandle(this.main.sliderHelperExtension.valueToPosition(this.main.viewLowValue));

			//b. update max and min handle element
			this.main.maxHandleLabelElement.show();
			this.main.minHandleLabelElement.show();
			this.main.combinedLabelElement.hide();
		}

		//3 - update visibility
		if (this.main.viewOptions.autoHideLimitLabels) this.updateFloorAndCeilLabelsVisibility();
	}


	/**------------------------------------------------------
	 * Selection Bar Helpers
	 */
	updateSelectionBar() {

		//0 - update position and dimension
		const isSelectionBarFromRight: boolean = this.main.viewOptions.rightToLeft
			? !this.main.viewOptions.showSelectionBarEnd
			: this.main.viewOptions.showSelectionBarEnd!;

		const positionForRange: number = this.main.viewOptions.rightToLeft
			? this.main.maxHandleElement.position + this.main.handleHalfDimension
			: this.main.minHandleElement.position + this.main.handleHalfDimension;

		//1 - dimension and position
		let position	: number = 0;
		let dimension	: number = 0;
		if (this.main.range) {
			dimension = Math.abs(this.main.maxHandleElement.position - this.main.minHandleElement.position);
			position = positionForRange;
		} else {

			if (Util.Basic.isDefined(this.main.viewOptions.showSelectionBarFromValue)) {

				//a. get center status and position
				const center				  : number  = this.main.viewOptions.showSelectionBarFromValue!;
				const centerPosition		  : number  = this.main.sliderHelperExtension.valueToPosition(center);
				const isModelGreaterThanCenter: boolean = this.main.viewOptions.rightToLeft ? this.main.viewLowValue <= center : this.main.viewLowValue > center;

				//b. update dimension and position
				if (isModelGreaterThanCenter) {
					dimension = this.main.minHandleElement.position - centerPosition;
					position = centerPosition + this.main.handleHalfDimension;
				} else {
					dimension = centerPosition - this.main.minHandleElement.position;
					position = this.main.minHandleElement.position + this.main.handleHalfDimension;
				}
			} else if (isSelectionBarFromRight) {
				dimension = Math.ceil(Math.abs(this.main.maxHandlePosition - this.main.minHandleElement.position) + this.main.handleHalfDimension);
				position = Math.floor(this.main.minHandleElement.position + this.main.handleHalfDimension);
			} else {
				dimension = this.main.minHandleElement.position + this.main.handleHalfDimension;
				position = 0;
			}
		}

		//2 - set element position and dimension
		this.main.selectionBarElement.setDimension(dimension);
		this.main.selectionBarElement.setPosition(position);
		if (this.main.range && this.main.viewOptions.showOuterSelectionBars) {

			// update element
			if (this.main.viewOptions.rightToLeft) {
				this.main.rightOuterSelectionBarElement.setDimension(position);
				this.main.rightOuterSelectionBarElement.setPosition(0);
				this.main.fullBarElement.calculateDimension();
				this.main.leftOuterSelectionBarElement.setDimension(this.main.fullBarElement.dimension - (position + dimension));
				this.main.leftOuterSelectionBarElement.setPosition(position + dimension);
			} else {
				this.main.leftOuterSelectionBarElement.setDimension(position);
				this.main.leftOuterSelectionBarElement.setPosition(0);
				this.main.fullBarElement.calculateDimension();
				this.main.rightOuterSelectionBarElement.setDimension(this.main.fullBarElement.dimension - (position + dimension));
				this.main.rightOuterSelectionBarElement.setPosition(position + dimension);
			}
		}

		//3 - color and bar style
		if (Util.Basic.isDefined(this.main.viewOptions.getSelectionBarColor)) {
			const color: string = this.getSelectionBarColor();
			this.main.barStyle = { backgroundColor: color };
		} else if (Util.Basic.isDefined(this.main.viewOptions.selectionBarGradient)) {

			const offset: number = (Util.Basic.isDefined(this.main.viewOptions.showSelectionBarFromValue))
				? this.main.sliderHelperExtension.valueToPosition(this.main.viewOptions.showSelectionBarFromValue!)
				: 0;

			const reversed: boolean = (offset - position > 0 && !isSelectionBarFromRight) || (offset - position <= 0 && isSelectionBarFromRight);
			const direction: string = this.main.viewOptions.vertical
				? reversed ? 'bottom' : 'top'
				: reversed ? 'left'   : 'right';

			this.main.barStyle = {
				backgroundImage: 'linear-gradient(to ' + direction + ', ' + this.main.viewOptions.selectionBarGradient!.from + ' 0%,' + this.main.viewOptions.selectionBarGradient!.to + ' 100%)',
			};

			if (this.main.viewOptions.vertical) {
				this.main.barStyle.backgroundPosition = 'center ' + (offset + dimension + position + (reversed ? -this.main.handleHalfDimension : 0)) + 'px';
				this.main.barStyle.backgroundSize 	  = '100% ' + (this.main.fullBarElement.dimension - this.main.handleHalfDimension) + 'px';
			} else {
				this.main.barStyle.backgroundPosition = offset - position + (reversed ? this.main.handleHalfDimension : 0) + 'px center';
				this.main.barStyle.backgroundSize 	  = this.main.fullBarElement.dimension - this.main.handleHalfDimension + 'px 100%';
			}
		}
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private normalizedModelValues(input: NgModelValues): NgModelValues {

		//0 - get normalized input
		const normalizedInput: NgModelValues = new NgModelValues();
		normalizedInput.value 	  = input.value;
		normalizedInput.highValue = input.highValue;

		//1 - if steps array is defined
		if (Util.Basic.isDefined(this.main.viewOptions.stepsArray)) {
			if (this.main.viewOptions.enforceStepsArray) {

				//a. get index and value
				const valueIndex: number = this.main.customizableSliderHelper.findStepIndex(normalizedInput.value, this.main.viewOptions.stepsArray!);
				normalizedInput.value 	 = this.main.viewOptions.stepsArray![valueIndex].value;

				//b. if range then set high value
				if (this.main.range) {
					const highValueIndex: number = this.main.customizableSliderHelper.findStepIndex(normalizedInput.highValue, this.main.viewOptions.stepsArray!);
					normalizedInput.highValue 	 = this.main.viewOptions.stepsArray![highValueIndex].value;
				}
			}

			return normalizedInput;
		}

		//2 - if enforce step set high value
		if (this.main.viewOptions.enforceStep) {
			normalizedInput.value = this.main.sliderHelperExtension.roundStep(normalizedInput.value);
			if (this.main.range) normalizedInput.highValue = this.main.sliderHelperExtension.roundStep(normalizedInput.highValue);
		}

		//3 - if enforce range
		if (this.main.viewOptions.enforceRange) {

			//a. set high value
			normalizedInput.value = this.main.customizableSliderHelper.clampToRange(normalizedInput.value, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);
			if (this.main.range) normalizedInput.highValue = this.main.customizableSliderHelper.clampToRange(normalizedInput.highValue, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);

			//b. make sure that range slider invariant (value <= highValue) is always satisfied
			if (this.main.range && input.value > input.highValue) {
				if (this.main.viewOptions.noSwitching) {
					normalizedInput.value = normalizedInput.highValue;
				} else {
					const tempValue: number = input.value;
					normalizedInput.value = input.highValue;
					normalizedInput.highValue = tempValue;
				}
			}
		}

		//4 - return the normalized inputs
		return normalizedInput;
	}

	private applyStepsArrayOptions() {

		//0 - set default view options
		this.main.viewOptions.floor = 0;
		this.main.viewOptions.ceil  = this.main.viewOptions.stepsArray!.length - 1;
		this.main.viewOptions.step  = 1;

		//1 - if translate then exit
		if (Util.Basic.isDefined(this.main.viewOptions.translate)) return;

		//2 - set translate function
		this.main.viewOptions.translate = (modelValue: number): string => {
			if (this.main.viewOptions.bindIndexForStepsArray) return String(this.main.sliderHelperExtension.getStepValue(modelValue));
			return String(modelValue);
		};
	}

	private applyFloorCeilOptions() {

		//0 - set view options step
		if (Util.Basic.isUndefined(this.main.viewOptions.step)) {
			this.main.viewOptions.step = 1;
		} else {
			this.main.viewOptions.step = +this.main.viewOptions.step!;
			if (this.main.viewOptions.step <= 0) this.main.viewOptions.step = 1;
		}

		//1 - on undefined ceil and floor value
		if (Util.Basic.isUndefined(this.main.viewOptions.ceil) || Util.Basic.isUndefined(this.main.viewOptions.floor)) throw new Error(`SliderValueUpdateExtension => applyFloorCeilOptions => FATAL ERROR: floor and ceil options must be supplied`);

		//2 - set ceil and floor value
		this.main.viewOptions.ceil = +this.main.viewOptions.ceil!;
		this.main.viewOptions.floor = +this.main.viewOptions.floor!;

		//3 - set translate function
		if (Util.Basic.isUndefined(this.main.viewOptions.translate)) this.main.viewOptions.translate = (value: number): string => String(value);
	}

	private refocusPointerIfNeeded() {

		//0 - if current focus pointer defined
		if (Util.Basic.isDefined(this.main.currentFocusPointer)) return;

		//1 - on pointer focus
		this.main.sliderEventsExtension.onPointerFocus(this.main.currentFocusPointer);
		const element: NgSliderHandleDirective = this.main.getPointerElement(this.main.currentFocusPointer);
		element?.focus();
	}

	//** Check label below floor */
	private isLabelBelowFloorLabel(label: NgSliderLabelDirective): boolean {
		const pos	  : number = label.position;
		const dim	  : number = label.dimension;
		const floorPos: number = this.main.floorLabelElement.position;
		const floorDim: number = this.main.floorLabelElement.dimension;
		return this.main.viewOptions.rightToLeft ? (pos + dim >= floorPos - 2) : (pos <= floorPos + floorDim + 2);
	}

	//** Check label above ceil */
	private isLabelAboveCeilLabel(label: NgSliderLabelDirective): boolean {
		const pos	  : number = label.position;
		const dim	  : number = label.dimension;
		const ceilPos : number = this.main.ceilLabelElement.position;
		const ceilDim : number = this.main.ceilLabelElement.dimension;
		return this.main.viewOptions.rightToLeft ? (pos <= ceilPos + ceilDim + 2) : (pos + dim >= ceilPos - 2);
	}

	//** Get selection bar color */
	getSelectionBarColor(): string {
		if (this.main.range) return this.main.viewOptions.getSelectionBarColor!(this.main.value, this.main.highValue);
		return this.main.viewOptions.getSelectionBarColor!(this.main.value);
	}

	private emitOutputs(modelChange: NgOutputModelChange) {

		//0 - emit outputs model change value
		this.main.valueChange.emit(modelChange.value);
		if (this.main.range) this.main.highValueChange.emit(modelChange.highValue);

		//1 - update change callback
		if (Util.Basic.isDefined(this.main.onChangeCallback)) {
			if (this.main.range) this.main.onChangeCallback([modelChange.value, modelChange.highValue]);
				else this.main.onChangeCallback(modelChange.value);
		}

		//2 - update touched callback
		if (Util.Basic.isDefined(this.main.onTouchedCallback)) {
			if (this.main.range) this.main.onTouchedCallback([modelChange.value, modelChange.highValue]);
				else this.main.onTouchedCallback(modelChange.value);
		}
	}
}
